import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import ProUpgradeNotice from "../../components/business/ProUpgradeNotice";

const EMPTY_FORM = {
  name: "",
  type: "flash_sale",
  discount_percent: 10,
  starts_at: "",
  ends_at: "",
  daily_start_time: "",
  daily_end_time: "",
  last_minute_hours: 24,
  service_ids: [],
};

const TYPE_LABELS = {
  flash_sale: "Flash sale",
  happy_hour: "Happy hour",
  last_minute: "Last-minute discount",
};

export default function Promotions() {
  const [promotions, setPromotions] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [proRequired, setProRequired] = useState(false);

  const load = () =>
    api
      .get("/owner/promotions")
      .then(({ data }) => setPromotions(data))
      .catch(() => setPromotions([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/owner/services").then(({ data }) => setServices(data)).catch(() => setServices([]));
  }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const startEdit = (promo) => {
    setEditingId(promo.id);
    setForm({
      name: promo.name,
      type: promo.type,
      discount_percent: promo.discount_percent,
      starts_at: promo.starts_at?.slice(0, 16) || "",
      ends_at: promo.ends_at?.slice(0, 16) || "",
      daily_start_time: promo.daily_start_time?.slice(0, 5) || "",
      daily_end_time: promo.daily_end_time?.slice(0, 5) || "",
      last_minute_hours: promo.last_minute_hours || 24,
      service_ids: promo.services?.map((s) => s.id) || [],
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      service_ids: f.service_ids.includes(id) ? f.service_ids.filter((s) => s !== id) : [...f.service_ids, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/owner/promotions/${editingId}`, form);
      } else {
        await api.post("/owner/promotions", form);
      }
      cancelForm();
      load();
    } catch (err) {
      if (err?.response?.status === 402) {
        setProRequired(true);
        cancelForm();
      }
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id) => {
    await api.delete(`/owner/promotions/${id}`);
    load();
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-montserrat text-2xl font-bold text-slate-900">Promotions</h1>
        <Button size="sm" onClick={() => (showForm ? cancelForm() : startCreate())}>
          <Plus className="h-4 w-4" /> Add promotion
        </Button>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Discounts apply automatically at checkout when a customer books a covered service during the
        promotion window.
      </p>

      {proRequired && (
        <div className="mb-4">
          <ProUpgradeNotice feature="Promotions" />
        </div>
      )}

      {showForm && (
        <Card className="mb-6 p-4">
          <p className="mb-3 text-sm font-semibold text-slate-900">{editingId ? "Edit promotion" : "New promotion"}</p>
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input placeholder="Promotion name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="h-11 rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-primary"
            >
              {Object.entries(TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <Input placeholder="Discount %" type="number" min={1} max={90} required value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} />

            {form.type !== "last_minute" && (
              <>
                <Input type="datetime-local" placeholder="Starts" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
                <Input type="datetime-local" placeholder="Ends" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} />
              </>
            )}
            {form.type === "happy_hour" && (
              <>
                <Input type="time" placeholder="Daily start" value={form.daily_start_time} onChange={(e) => setForm({ ...form, daily_start_time: e.target.value })} />
                <Input type="time" placeholder="Daily end" value={form.daily_end_time} onChange={(e) => setForm({ ...form, daily_end_time: e.target.value })} />
              </>
            )}
            {form.type === "last_minute" && (
              <Input type="number" placeholder="Within X hours of the slot" value={form.last_minute_hours} onChange={(e) => setForm({ ...form, last_minute_hours: e.target.value })} />
            )}

            <div className="sm:col-span-2">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Applies to</p>
              <div className="flex flex-wrap gap-2">
                {services.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => toggleService(s.id)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                      form.service_ids.includes(s.id) ? "border-primary bg-primary/5 text-primary" : "border-border text-slate-600"
                    }`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" disabled={saving || form.service_ids.length === 0}>
                {saving ? "Saving…" : editingId ? "Save changes" : "Save promotion"}
              </Button>
              <Button type="button" variant="outline" onClick={cancelForm}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : promotions.length === 0 ? (
        <p className="text-muted-foreground">No promotions yet — create one above.</p>
      ) : (
        <div className="divide-y divide-border rounded-2xl border border-border">
          {promotions.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div>
                <p className="font-medium text-slate-900">{p.name}</p>
                <p className="text-xs text-muted-foreground">
                  {TYPE_LABELS[p.type]} · {p.services?.map((s) => s.name).join(", ")}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-semibold text-slate-900">-{p.discount_percent}%</span>
                <Badge variant={p.is_active ? "accent" : "default"}>{p.is_active ? "Active" : "Inactive"}</Badge>
                <button onClick={() => startEdit(p)} className="text-muted-foreground hover:text-primary">
                  <Pencil className="h-4 w-4" />
                </button>
                {p.is_active && (
                  <button onClick={() => deactivate(p.id)} className="text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
